import { onIdTokenChanged } from "firebase/auth";
import jwtDecode from "jwt-decode";
import { useEffect, useRef, useState } from "react";
import TagManager from "react-gtm-module";
import { Outlet, ScrollRestoration, useLocation } from "react-router-dom";

import config from "@/config";
import { setUnregisteredUserConfig } from "@/config/unregisteredUser.config";
import PaymentProfileProvider from "@/context/PaymentProfileProvider";
import ProfileDataContext from "@/context/ProfileDataContext";
import useGetSearchParams from "@/hooks/useGetSearchParams";
import useProtectActionUnregistered from "@/hooks/useProtectActionUnregistered";
import useStateHandler from "@/hooks/useStateHandler";
import { auth, resolveGoogleRedirectResult } from "@/services/firebase.service";
import { exchangeGoogleUserForSession } from "@/services/google-auto-flow.service";
import GuestsService from "@/services/guests.service";
import UserService from "@/services/user.service";
import AuthPopup from "@/shared-components/popups/auth-popup";
// eslint-disable-next-line no-unused-vars
import CookiesPopup from "@/shared-components/popups/components/CookiesPopup";
import DownloadAppPopup from "@/shared-components/popups/components/DownloadAppPopup";
import TemporalPopup from "@/shared-components/popups/components/TemporalPopup";
import UnregisteredPopup from "@/shared-components/popups/unregistered-user-popup";
import Utils from "@/utils";
import { getSuarteEnvironment } from "@/utils/environment.utils";
import { syncStatusBarsWithMode } from "@/utils/themeBridge";

const tagManagerArgs = {
  gtmId: config.app.gTagId,
};

const shouldInitializeTagManager = process.env.NODE_ENV === "production" && Boolean(config.app.gTagId);

const DOWNLOAD_APP_POPUP_DELAY = 2 * 60 * 1000; // 2 minutes download popup delay
const DOWNLOAD_APP_POPUP_STORAGE_KEY = "downloadAppPopup.hide";

if (shouldInitializeTagManager) {
  TagManager.initialize(tagManagerArgs);
}

export default function AppInit() {
  const { stateHandler, cacheHandler, state } = useStateHandler();
  const location = useLocation();
  const isResetPasswordRoute = location?.pathname === "/reset-password";
  const [params] = useGetSearchParams({ validParams: ["invite"] });
  const { showPopup: showGlobalMandatoryPopup } = useProtectActionUnregistered({
    typeOfProtection: "USER_LIMIT_VIEW",
    isEnabled: !isResetPasswordRoute,
  });

  const profileData = state.user_session || null;
  const [downloadPopupVisible, setDownloadPopupVisible] = useState(false);
  const downloadAppPopupTimer = useRef(null);
  const resumedRedirectUid = useRef(null);
  const hasAttemptedRedirectResume = useRef(false);

  async function processRedirectedFirebaseUser(user, inviteCode) {
    if (!user) {
      return { ok: false, reason: "missing-user" };
    }

    const result = await exchangeGoogleUserForSession({
      firebaseUser: user,
      inviteCode,
      stateHandler,
      setLoading: null,
    });

    if (!result.ok) {
      if (result.reason === "email-belongs-to-artist") {
        alert("This email is already associated with an artist");
      } else if (result.reason !== "missing-user") {
        console.error("[AppInit] Failed to resume Google login", result);
      }
    }

    return result;
  }

  useEffect(() => {
    let isMounted = true;
    let resolvingRedirect = false;
    const RESUME_MAX_ATTEMPTS = 4;
    const RESUME_RETRY_DELAY = 600; // ms

    const pause = (duration) => new Promise((resolve) => setTimeout(resolve, duration));

    async function tryResolveRedirect() {
      let redirectResult = null;
      for (let attempt = 1; attempt <= RESUME_MAX_ATTEMPTS; attempt += 1) {
        redirectResult = await resolveGoogleRedirectResult();

        if (redirectResult?.user) {
          return redirectResult;
        }

        if (attempt < RESUME_MAX_ATTEMPTS) {
          await pause(RESUME_RETRY_DELAY);
        }
      }

      return redirectResult;
    }

    async function resumeGoogleLoginIfNeeded() {
      if (resolvingRedirect || hasAttemptedRedirectResume.current) {
        return;
      }
      resolvingRedirect = true;
      try {
        const inviteCode = state.invite || params?.invite || null;
        const redirectResult = await tryResolveRedirect();
        const redirectedUser = redirectResult?.user || auth.currentUser;

        if (!redirectedUser || !isMounted) {
          return;
        }

        if (resumedRedirectUid.current === redirectedUser.uid) {
          return;
        }

        const result = await processRedirectedFirebaseUser(redirectedUser, inviteCode);
        if (result?.ok) resumedRedirectUid.current = redirectedUser.uid;
      }
      catch (error) {
        console.error("[AppInit] Failed to resume Google login", error);
      }
      finally {
        hasAttemptedRedirectResume.current = true;
        resolvingRedirect = false;
      }
    }

    resumeGoogleLoginIfNeeded();

    return () => {
      isMounted = false;
    };
  }, [params, state.invite]);

  useEffect(() => {
    let mounted = true;
    let authChangeVersion = 0;

    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      const requestVersion = ++authChangeVersion;

      if (user) {
        const decodedToken = jwtDecode(user.accessToken);

        if (decodedToken._id) {
          /** We check if the token is from our custom token login */
          const { response, data } = await UserService.getUserSession();

          // A sign-in can replace the Firebase user while a previous session
          // request is still in flight. Only the latest auth change may write
          // global session state; otherwise the artwork entry view briefly
          // renders with stale viewer data before correcting itself.
          if (
            mounted
            && authChangeVersion === requestVersion
            && auth.currentUser?.uid === user.uid
            && response.ok
          ) {
            stateHandler.set("user_session", data);
          }
        }
      }
      else if (mounted && authChangeVersion === requestVersion) {
        stateHandler.set("user_session", null);
        cacheHandler.clearCache();
      }
    });

    if (params?.invite) {
      stateHandler.set("invite", params.invite);
    }

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [cacheHandler, params?.invite, stateHandler]);

  useEffect(() => {
    const root = document.documentElement;
    if (state.user_session) {
      const { theme, mode } = state.user_session.user_preferences || {};
      if (theme) root.setAttribute("data-theme", theme);
      if (mode) {
        root.setAttribute("data-mode", mode);
      } else if (!root.getAttribute("data-mode")) {
        root.setAttribute("data-mode", "dark");
      }
    }
    else {
      root.setAttribute("data-theme", "starry_moon");
      root.setAttribute("data-mode", "dark");
    }

    const appliedMode = root.getAttribute("data-mode") === "light" ? "light" : "dark";
    syncStatusBarsWithMode(appliedMode);
  }, [state.user_session]);

  useEffect(() => {
    async function getConfigurationGuests() {
      if (!state.user_session) {
        const { data } = await GuestsService.getConfigurationGuests();
        setUnregisteredUserConfig(data);
        stateHandler.set("turnstile_site_key", data.turnstile_site_key || null);
        Utils.preloadImages([`${data.guest_image}/w=2200`]);
      }
    }
    getConfigurationGuests();
  }, []);

  useEffect(() => {
    Utils.preloadImages([
      "07e0bb36-e259-4df0-e382-4c78834aa700/w=300",
      "13a34fe1-4954-46e9-4a6a-f79af8985d00/public",
      "73d03466-3a94-4da0-5a38-163895a91200/w=1000,blur=20",
      "1dc634fd-ff91-4e23-0227-9e2e4437f700/w=500,blur=20",
      "287f3610-3513-446d-5779-91763bdbe000/w=2000",
      "GzjV6OCKjJgqiM4UmTMhqC3wbAp2_648ebb932a82c10af7e7f1c6/w=2200",
    ]);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const env = getSuarteEnvironment();
    if (env.isApp) {
      return undefined;
    }

    const hasHiddenPopup = localStorage.getItem(DOWNLOAD_APP_POPUP_STORAGE_KEY) === "true";
    if (hasHiddenPopup) {
      return undefined;
    }

    downloadAppPopupTimer.current = window.setTimeout(() => {
      setDownloadPopupVisible(true);
      downloadAppPopupTimer.current = null;
    }, DOWNLOAD_APP_POPUP_DELAY);

    return () => {
      if (downloadAppPopupTimer.current) {
        clearTimeout(downloadAppPopupTimer.current);
        downloadAppPopupTimer.current = null;
      }
    };
  }, []);

  function handleDownloadAppPopupClose(doNotShowAgain = true) {
    if (downloadAppPopupTimer.current) {
      clearTimeout(downloadAppPopupTimer.current);
      downloadAppPopupTimer.current = null;
    }

    if (doNotShowAgain && typeof window !== "undefined") {
      localStorage.setItem(DOWNLOAD_APP_POPUP_STORAGE_KEY, "true");
    }

    setDownloadPopupVisible(false);
  }

  return (
    <ProfileDataContext.Provider
      value={{
        profileData,
        setProfileData: (data) => stateHandler.set("user_session", data),
      }}
    >
      <PaymentProfileProvider>
        <Outlet />
        {showGlobalMandatoryPopup && (
          <UnregisteredPopup showPopUp={showGlobalMandatoryPopup} />
        )}
        {/* <CookiesPopup/> */}
        <AuthPopup />
        <TemporalPopup />
        <DownloadAppPopup
          open={downloadPopupVisible}
          onClose={handleDownloadAppPopupClose}
        />
        <ScrollRestoration />
      </PaymentProfileProvider>
    </ProfileDataContext.Provider>
  );
}
